import React from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../common/Input';
import LocationPicker from './LocationPicker';

interface InstallationFormData {
  latitude: number;
  longitude: number;
  azimuth1?: number;
  tilt1?: number;
  panelCount1?: number;
  panelSize1?: number;
  efficiency1?: number;
  azimuth2?: number;
  tilt2?: number;
  panelCount2?: number;
  panelSize2?: number;
  efficiency2?: number;
  azimuth3?: number;
  tilt3?: number;
  panelCount3?: number;
  panelSize3?: number;
  efficiency3?: number;
  azimuth4?: number;
  tilt4?: number;
  panelCount4?: number;
  panelSize4?: number;
  efficiency4?: number;
}

interface InstallationFormProps {
  data: InstallationFormData;
  onChange: (field: keyof InstallationFormData, value: number | undefined) => void;
  errors?: Record<string, string>;
  maxStrings?: number; // Maximum strings allowed by plan
  activeStrings?: number; // Currently active strings
  onActiveStringsChange?: (count: number) => void;
}

const InstallationForm: React.FC<InstallationFormProps> = ({
  data,
  onChange,
  errors = {},
  maxStrings = 4,
  activeStrings = 1,
  onActiveStringsChange,
}) => {
  const { t } = useTranslation();
  const handleChange =
    (field: keyof InstallationFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(',', '.');
      const value = raw === '' ? undefined : Number(raw);
      onChange(field, value);
    };

  const renderStringFields = (stringNum: 1 | 2 | 3 | 4) => {
    const azimuthKey = `azimuth${stringNum}` as keyof InstallationFormData;
    const tiltKey = `tilt${stringNum}` as keyof InstallationFormData;
    const panelCountKey = `panelCount${stringNum}` as keyof InstallationFormData;
    const panelSizeKey = `panelSize${stringNum}` as keyof InstallationFormData;
    const efficiencyKey = `efficiency${stringNum}` as keyof InstallationFormData;

    return (
      <div className="space-y-4">
        <h4 className="font-medium text-gray-700">
          {t('components.string')} {stringNum}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t('components.azimuthDeg')}
            type="number"
            min="0"
            max="360"
            step="1"
            value={data[azimuthKey] ?? ''}
            onChange={handleChange(azimuthKey)}
            error={errors[azimuthKey]}
          />
          <Input
            label={t('components.tiltDeg')}
            type="number"
            min="0"
            max="90"
            step="1"
            value={data[tiltKey] ?? ''}
            onChange={handleChange(tiltKey)}
            error={errors[tiltKey]}
          />
          <Input
            label={t('components.panelCount')}
            type="number"
            min="0"
            step="1"
            value={data[panelCountKey] ?? ''}
            onChange={handleChange(panelCountKey)}
            error={errors[panelCountKey]}
          />
          <Input
            label={t('components.panelSizeM2')}
            type="number"
            min="0"
            step="0.01"
            value={data[panelSizeKey] ?? ''}
            onChange={handleChange(panelSizeKey)}
            error={errors[panelSizeKey]}
          />
          <Input
            label={t('components.efficiencyPct')}
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={data[efficiencyKey] ?? ''}
            onChange={handleChange(efficiencyKey)}
            error={errors[efficiencyKey]}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Location */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('components.location')}</h3>
        <LocationPicker
          latitude={data.latitude ?? 0}
          longitude={data.longitude ?? 0}
          onLocationChange={(lat, lon) => {
            onChange('latitude', Math.round(lat * 1e6) / 1e6);
            onChange('longitude', Math.round(lon * 1e6) / 1e6);
          }}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <Input
            label={t('components.latitude')}
            type="number"
            min="-90"
            max="90"
            step="any"
            value={data.latitude ?? ''}
            onChange={handleChange('latitude')}
            error={errors.latitude}
            required
          />
          <Input
            label={t('components.longitude')}
            type="number"
            min="-180"
            max="180"
            step="any"
            value={data.longitude ?? ''}
            onChange={handleChange('longitude')}
            error={errors.longitude}
            required
          />
        </div>
      </div>

      {/* String Configuration */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('components.stringConfig')}</h3>

        {/* Active Strings Selector */}
        {onActiveStringsChange && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('components.howManyStrings', { count: maxStrings })}
            </label>
            <select
              value={activeStrings}
              onChange={(e) => onActiveStringsChange(Number(e.target.value))}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              {Array.from({ length: maxStrings }, (_, i) => i + 1).map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-600">{t('components.configureLater')}</p>
          </div>
        )}

        <div className="space-y-6">
          {Array.from({ length: activeStrings }, (_, i) => i + 1).map((stringNum, index) => (
            <div key={stringNum}>
              {index > 0 && <div className="border-t border-gray-200 pt-6" />}
              {renderStringFields(stringNum as 1 | 2 | 3 | 4)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InstallationForm;
